/**
 * fiduciary-config.ts — constants for the Fiduciary entity dialog.
 *
 * Source of truth: `docs/port-design/category-cards/fiduciary.md`.
 *
 * NOTE: the card is a SKELETON — §15 walkthrough deferred. Per Bill's
 * 2026-04-24 build card: ship the scaffolding + best-effort defaults, flag
 * every unclear item with `TODO(bill): fiduciary walkthrough needed`.
 *
 * Per Bill's 2026-04-23 admin-configurable architecture mandate, these MUST
 * eventually move to SQL catalog tables:
 *   - Fiduciary sub-type picker → `EntityTypes` SQL catalog
 *   - Appointment-authority list → `LookupValues` catalog
 *   - Public Administrator roster → `PublicAdministrators` SQL catalog
 *     (fiduciary.md Open Question #4)
 *
 * For v0.1.37 these are hardcoded here; port phase 2 moves them behind the
 * `useConfig()` bundle in `/v3/config/*` responses.
 */

// -----------------------------------------------------------------------------
// Entity sub-types — per build-card spec (3 primary: RepPayee / Conservator /
// VAFiduciary). MedicalSetAside + USDAGrant exist in the 5-type card but are
// out of scope for this scaffold; added as TODO placeholders below.
// -----------------------------------------------------------------------------

export interface FiduciarySubTypeOption {
  /** RaoPerson.customerType value — note shared-types CustomerType enum
      doesn't include these yet, so we cast at the persist boundary. */
  readonly value: string;
  readonly label: string;
  /** Short helper text shown under the picker when this value is selected. */
  readonly hint?: string;
  /** Legacy Call Class (Core Director Field 23) — CALL_CLASS_MAP per card. */
  readonly callClass?: string;
  /** Which appointment authority is canonical for this sub-type. */
  readonly canonicalAuthority?: FiduciaryAppointmentAuthority;
}

export const FIDUCIARY_SUB_TYPES: ReadonlyArray<FiduciarySubTypeOption> = [
  {
    value: 'RepPayee',
    label: 'Representative Payee (SSA)',
    hint:
      'SSA appointment letter required. Protected person CANNOT have OLB or debit card. ' +
      'TinCode=S (beneficiary\'s SSN). Consumer flag = Y.',
    callClass: '2',
    canonicalAuthority: 'SSA',
  },
  {
    value: 'Conservator',
    label: 'Conservator (Court-appointed)',
    hint:
      'Court appointment order required. Protected person CANNOT have OLB or debit card. ' +
      'Manager approval required (court-order review). $5,000 hold if restricted.',
    callClass: '1',
    canonicalAuthority: 'Court',
  },
  {
    value: 'VAFiduciary',
    label: 'VA Fiduciary',
    hint:
      'VA appointment packet required. Veteran signing authority = "Not Authorized"; ' +
      'Fiduciary = "Authorized". Manager approval required (VA appointment verification). ' +
      'Consumer flag = N.',
    callClass: '7',
    canonicalAuthority: 'VA',
  },
  // TODO(bill): fiduciary walkthrough needed — MedicalSetAside + USDAGrant
  // are in the card but not in the build-card scope. Add when Bill walks §15.
  // {
  //   value: 'MedicalSetAside',
  //   label: 'Medical Set Aside (MSA)',
  //   callClass: '8',
  //   canonicalAuthority: 'Other',
  // },
  // {
  //   value: 'USDAGrant',
  //   label: 'USDA Grant',
  //   callClass: '12',
  //   canonicalAuthority: 'Other',
  // },
];

/** Fast-lookup by value. */
export const FIDUCIARY_SUB_TYPE_BY_VALUE: Record<string, FiduciarySubTypeOption> =
  Object.fromEntries(FIDUCIARY_SUB_TYPES.map((t) => [t.value, t]));

// -----------------------------------------------------------------------------
// Appointment authority — drives which reference-number field is required.
// -----------------------------------------------------------------------------

export type FiduciaryAppointmentAuthority = 'Court' | 'SSA' | 'VA' | 'Other';

export interface FiduciaryAppointmentAuthorityOption {
  readonly value: FiduciaryAppointmentAuthority;
  readonly label: string;
  readonly hint?: string;
}

export const FIDUCIARY_APPOINTMENT_AUTHORITIES: ReadonlyArray<FiduciaryAppointmentAuthorityOption> = [
  {
    value: 'Court',
    label: 'Court (State Probate / Circuit)',
    hint: 'Court-appointed conservator. Case # required. IdVerifyCode candidate = 6 (State Documents).',
  },
  {
    value: 'SSA',
    label: 'Social Security Administration',
    hint: 'SSA Rep Payee appointment. SSA Claim # required. IdVerifyCode candidate = 8 (Other).',
  },
  {
    value: 'VA',
    label: 'Department of Veterans Affairs',
    hint: 'VA Fiduciary appointment. VA File # required. IdVerifyCode candidate = 8 (Other).',
  },
  {
    value: 'Other',
    label: 'Other / MSA / USDA',
    hint: 'Medical Set Aside, USDA Grant, or other non-standard appointment authority.',
  },
];

// -----------------------------------------------------------------------------
// Supervisor-approval gate — sub-types that require Manager signoff
// per seed 11-entity-rules.sql:228-232.
// -----------------------------------------------------------------------------

export const SUPERVISOR_APPROVAL_SUBTYPES: ReadonlySet<string> = new Set([
  'Conservator',
  'VAFiduciary',
  // 'USDAGrant', // TODO(bill): fiduciary walkthrough needed — out of current scope
  // 'MedicalSetAside', // TODO(bill): fiduciary walkthrough needed — seed gap per card
]);

// -----------------------------------------------------------------------------
// Helper text banners — surfaced contextually in Identity section.
// -----------------------------------------------------------------------------

export const FIDUCIARY_HELPER_TEXT = {
  subTypeIntro:
    'Select the fiduciary sub-type. This controls which appointment document is required, ' +
    'which approval gates apply, and which restricted services auto-revoke on save.',

  wardIntro:
    'The protected person (beneficiary / ward / veteran) is the person whose funds are being ' +
    'managed. If they already have a CIF, paste the CIF ID below; otherwise leave blank and ' +
    'add them as a second Person on the cart after save.',

  // TODO(bill): fiduciary walkthrough needed — confirm whether the beneficiary
  // CIF link lives on `protectedPersonCifId`, `conservateeCifId`, or a new
  // `wardCifId` field. Card §Open Questions #8: model choice is unresolved.
  appointmentIntro:
    'Appointment document number (court case #, SSA claim #, or VA file #) maps to ' +
    'Core Director Field 255. Appointment date → Field 257.',

  cipIntro:
    'Capture the FIDUCIARY\'s photo ID (driver license, passport, etc). The fiduciary IS a ' +
    'human; this is their individual CIP — distinct from the appointment instrument. The ' +
    'protected person\'s ID requirements are relaxed per procedure (DL not required).',
} as const;

// -----------------------------------------------------------------------------
// Primary-ID doc-code mapping (mirror of attachedDocs.ts — retained here for
// parity with business-config.PRIMARY_DOC_CODE_BY_SUBTYPE; the runtime
// resolver still calls getPrimaryIdDocCode() on the entity's customerType).
// -----------------------------------------------------------------------------

export const PRIMARY_DOC_CODE_BY_SUBTYPE: Record<string, string> = {
  RepPayee:     'REP_PAYEE_ID',
  Conservator:  'CONSERVATOR_ID',
  VAFiduciary:  'FIDUCIARY_ID',
  // TODO(bill): fiduciary walkthrough needed — MSA / USDA docCodes pending.
};

// -----------------------------------------------------------------------------
// Appointment reference-number field labels per sub-type.
// -----------------------------------------------------------------------------

export interface AppointmentRefFieldSpec {
  readonly label: string;
  readonly placeholder: string;
  readonly hint: string;
}

export const APPOINTMENT_REF_FIELDS: Record<string, AppointmentRefFieldSpec> = {
  RepPayee: {
    label: 'SSA Claim #',
    placeholder: '123-45-6789A',
    hint: 'Social Security claim number from the SSA appointment letter.',
  },
  Conservator: {
    label: 'Court Case #',
    placeholder: '24PR-CC00012',
    hint: 'Probate case number from the court order (state-county-format varies).',
  },
  VAFiduciary: {
    label: 'VA File #',
    placeholder: 'CSS 12 345 678',
    hint: 'VA file number from the fiduciary appointment packet.',
  },
  // TODO(bill): fiduciary walkthrough needed — MSA agreement # / USDA award #
  // formats pending §15 walkthrough.
};
